/**
 * FormField component - wrapper for form inputs with label and helper text
 *
 * Provides consistent layout and styling for form fields.
 * Styled with Tailwind CSS for dark theme.
 */

import type { FormFieldOptions, HelperTextOptions, ValidationState } from '../types/form';
import { createLabel, Label } from './label';
import { createHelperText, HelperText } from './helper-text';

/**
 * CSS classes for the form field wrapper
 */
const WRAPPER_CLASSES = 'flex flex-col gap-1.5';

/**
 * Create a FormField wrapper element
 *
 * @param input - The form input element to wrap
 * @param options - Form field options (label, helper text)
 * @returns The wrapper element containing label, input, and helper text
 */
export function createFormField(
  input: HTMLElement,
  options: FormFieldOptions = {}
): HTMLDivElement {
  const {
    label,
    helperText,
    required = false,
    className = '',
  } = options;

  // Create wrapper
  const wrapper = document.createElement('div');
  wrapper.className = `${WRAPPER_CLASSES} ${className}`.trim();

  // Add label if provided
  if (label) {
    // Get input ID for htmlFor
    const inputId = input.id || (input.querySelector('input, select, textarea') as HTMLElement)?.id;

    const labelElement = createLabel({
      ...label,
      htmlFor: label.htmlFor || inputId,
      required: label.required !== undefined ? label.required : required,
    });
    wrapper.appendChild(labelElement);
  }

  // Add input
  wrapper.appendChild(input);

  // Add helper text if provided
  if (helperText) {
    const helperElement = createHelperText(helperText);
    wrapper.appendChild(helperElement);
  }

  return wrapper;
}

/**
 * FormField class for object-oriented usage
 *
 * Provides a convenient way to manage form fields with labels and helper text.
 */
export class FormField {
  private wrapper: HTMLDivElement;
  private labelElement: Label | null = null;
  private helperTextElement: HelperText | null = null;
  private inputElement: HTMLElement;
  private options: FormFieldOptions;

  constructor(input: HTMLElement, options: FormFieldOptions = {}) {
    this.inputElement = input;
    this.options = { ...options };

    // Create wrapper
    this.wrapper = document.createElement('div');
    this.wrapper.className = `${WRAPPER_CLASSES} ${options.className || ''}`.trim();

    // Add label if provided
    if (options.label) {
      const inputId = input.id || (input.querySelector('input, select, textarea') as HTMLElement)?.id;
      const isRequired = options.label.required !== undefined ? options.label.required : (options.required || false);

      this.labelElement = new Label({
        ...options.label,
        htmlFor: options.label.htmlFor || inputId,
        required: isRequired,
      });
      this.wrapper.appendChild(this.labelElement.getElement());
    }

    // Add input
    this.wrapper.appendChild(input);

    // Add helper text if provided
    if (options.helperText) {
      this.helperTextElement = new HelperText(options.helperText);
      this.wrapper.appendChild(this.helperTextElement.getElement());
    }
  }

  /**
   * Get the wrapper element
   */
  getElement(): HTMLDivElement {
    return this.wrapper;
  }

  /**
   * Get the input element
   */
  getInputElement(): HTMLElement {
    return this.inputElement;
  }

  /**
   * Get the label element
   */
  getLabel(): Label | null {
    return this.labelElement;
  }

  /**
   * Get the helper text element
   */
  getHelperText(): HelperText | null {
    return this.helperTextElement;
  }

  /**
   * Set helper text content and type
   */
  setHelperText(text: string, type: HelperTextOptions['type'] = 'default'): this {
    if (this.helperTextElement) {
      this.helperTextElement.setText(text);
      this.helperTextElement.setType(type);
    } else {
      // Create helper text if it doesn't exist
      this.helperTextElement = new HelperText({ text, type });
      this.wrapper.appendChild(this.helperTextElement.getElement());
    }
    return this;
  }

  /**
   * Show error message and update validation state
   */
  setError(message: string): this {
    this.setHelperText(message, 'error');
    this.helperTextElement?.show();

    // Update input validation state if it has setValidationState method
    const inputWithValidation = this.inputElement as HTMLElement & {
      setValidationState?: (state: ValidationState) => void;
    };
    if (typeof inputWithValidation.setValidationState === 'function') {
      inputWithValidation.setValidationState('error');
    }

    return this;
  }

  /**
   * Show success message and update validation state
   */
  setSuccess(message?: string): this {
    if (message) {
      this.setHelperText(message, 'success');
      this.helperTextElement?.show();
    } else {
      this.helperTextElement?.hide();
    }

    // Update input validation state if it has setValidationState method
    const inputWithValidation = this.inputElement as HTMLElement & {
      setValidationState?: (state: ValidationState) => void;
    };
    if (typeof inputWithValidation.setValidationState === 'function') {
      inputWithValidation.setValidationState('success');
    }

    return this;
  }

  /**
   * Clear validation state and reset helper text
   */
  clearValidation(): this {
    if (this.helperTextElement) {
      if (this.options.helperText) {
        this.helperTextElement.setText(this.options.helperText.text);
        this.helperTextElement.setType(this.options.helperText.type || 'default');
        this.helperTextElement.show();
      } else {
        this.helperTextElement.hide();
      }
    }

    // Update input validation state if it has setValidationState method
    const inputWithValidation = this.inputElement as HTMLElement & {
      setValidationState?: (state: ValidationState) => void;
    };
    if (typeof inputWithValidation.setValidationState === 'function') {
      inputWithValidation.setValidationState('default');
    }

    return this;
  }

  /**
   * Hide the helper text
   */
  hideHelperText(): this {
    this.helperTextElement?.hide();
    return this;
  }

  /**
   * Show the helper text
   */
  showHelperText(): this {
    this.helperTextElement?.show();
    return this;
  }
}

/**
 * Utility to create a complete form field with input, label, and helper text
 *
 * @example
 * ```ts
 * const emailField = createCompleteFormField({
 *   inputType: 'input',
 *   inputOptions: {
 *     type: 'email',
 *     placeholder: 'Enter your email',
 *     required: true,
 *   },
 *   label: { text: 'Email Address' },
 *   helperText: { text: 'We will never share your email.' },
 * });
 *
 * form.appendChild(emailField.getElement());
 * ```
 */
export interface CompleteFormFieldOptions extends FormFieldOptions {
  inputType: 'input' | 'select' | 'textarea' | 'checkbox' | 'radio-group' | 'date-picker';
  inputOptions?: Record<string, unknown>;
}

/**
 * Helper function to create form fields inline in a more concise way
 */
export function wrapWithLabel(
  input: HTMLElement,
  labelText: string,
  options: {
    required?: boolean;
    helperText?: string;
    helperType?: HelperTextOptions['type'];
  } = {}
): HTMLDivElement {
  const fieldOptions: FormFieldOptions = {
    label: { text: labelText },
  };

  if (options.required !== undefined) {
    fieldOptions.required = options.required;
  }

  if (options.helperText) {
    fieldOptions.helperText = {
      text: options.helperText,
      type: options.helperType || 'default',
    };
  }

  return createFormField(input, fieldOptions);
}
